import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { invoiceDeepLink, jobDeepLink, INVOICE_PARAM, JOB_PARAM } from '../utils/recordLinks';

/**
 * The two halves of a crossing: going to the other record, and arriving at it.
 *
 * <p>Both directions use the same pair, so the behaviour cannot differ between them —
 * only the route and the parameter do. The cards themselves know no router: a page
 * hands them the navigation, which is what lets the job card be mounted bare by tests
 * and rendered by five different pages.
 */

/** Navigate to a record's page with it open. */
const useOpenRecord = (linkFor) => {
    const navigate = useNavigate();
    return useCallback((uuid) => navigate(linkFor(uuid)), [navigate, linkFor]);
};

/** To the invoice, on the Invoices page, opened. */
export const useOpenInvoice = () => useOpenRecord(invoiceDeepLink);

/** To the job, on the Jobs board, opened over it. */
export const useOpenJob = () => useOpenRecord(jobDeepLink);

/**
 * Arriving: open whatever `?<param>=<uuid>` names, once.
 *
 * <p>🔑 The parameter is CONSUMED — dropped from the URL as soon as it is used — so
 * closing the record and reloading does not reopen it, and the back button behaves.
 * `onOpen` must be stable (a `useCallback`), or this fires on every render.
 *
 * @param {string} param `invoice` or `job`
 * @param {(uuid: string) => void} onOpen what the page does to show that record
 */
export const useConsumeRecordParam = (param, onOpen) => {
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const wanted = searchParams.get(param);
        if (!wanted) return;
        onOpen(wanted);
        const next = new URLSearchParams(searchParams);
        next.delete(param);
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, param, onOpen]);
};

export { INVOICE_PARAM, JOB_PARAM };
